import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { Channel, Swatch } from '@bccomms/shared';
import { session } from '../../lib/session';
import { useStore } from '../../store';
import { Field, Modal, SwatchPicker } from '../ui';

type Draft = Omit<Channel, 'id'> & { id?: string };
const blank = (order: number): Draft => ({ name: '', short: '', kind: 'partyline', color: 'cyan', dimDb: -12, programChannelId: null, order });

export function ChannelsTab() {
  const channels = useStore((s) => s.channels);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!draft) return;
    setError(null);
    try {
      await session.admin('admin.channels.upsert', { channel: draft });
      setDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };
  const remove = async (c: Channel) => {
    if (!confirm(`Delete channel ${c.name}? It will be removed from every panel.`)) return;
    await session.admin('admin.channels.delete', { id: c.id }).catch((e: Error) => alert(e.message));
  };

  return (
    <>
      <div className="admin__title">
        <h2>Channels</h2>
        <button className="btn btn--primary" onClick={() => setDraft(blank(channels.length))}>
          <Plus size={16} /> New channel
        </button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Key label</th>
            <th>Type</th>
            <th>IFB dim</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {channels.map((c) => (
            <tr key={c.id}>
              <td className="mono">{c.order}</td>
              <td>
                <span className="row">
                  <span className="dot" data-sw={c.color} style={{ background: 'var(--sw)' }} /> {c.name}
                </span>
              </td>
              <td className="mono">{c.short}</td>
              <td>{c.kind === 'ifb' ? <span className="chip chip--warning">IFB</span> : c.kind === 'program' ? <span className="chip chip--brand">Program</span> : <span className="chip">Partyline</span>}</td>
              <td className="mono">{c.kind === 'ifb' ? `${c.dimDb} dB · ${channels.find((x) => x.id === c.programChannelId)?.short ?? 'no PGM'}` : '—'}</td>
              <td>
                <div className="table__actions">
                  <button className="iconbtn" aria-label="Edit" onClick={() => setDraft({ ...c })}>
                    <Pencil size={16} />
                  </button>
                  <button className="iconbtn" aria-label="Delete" onClick={() => void remove(c)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {draft && (
        <Modal
          title={draft.id ? 'Edit channel' : 'New channel'}
          onClose={() => setDraft(null)}
          actions={
            <>
              <button className="btn btn--neutral" onClick={() => setDraft(null)}>
                Cancel
              </button>
              <button className="btn btn--primary" disabled={!draft.name || !draft.short} onClick={() => void save()}>
                Save
              </button>
            </>
          }
        >
          <Field label="Name">
            <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field label="Key label (2–6 chars)">
            <input className="input" maxLength={6} value={draft.short} onChange={(e) => setDraft({ ...draft, short: e.target.value.toUpperCase() })} />
          </Field>
          <Field label="Type" help="Partyline: everyone talks and listens. Program: a listen-only feed from a mixer output. IFB: carries a program feed, dimmed whenever someone talks on it.">
            <select className="select" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as Channel['kind'] })}>
              <option value="partyline">Partyline</option>
              <option value="program">Program feed (listen-only source)</option>
              <option value="ifb">IFB (program + interrupts)</option>
            </select>
          </Field>
          {draft.kind === 'ifb' && (
            <Field label="Program source" help="Listeners of this IFB hear this program feed between interruptions.">
              <select className="select" value={draft.programChannelId ?? ''} onChange={(e) => setDraft({ ...draft, programChannelId: e.target.value || null })}>
                <option value="">None (interrupts only)</option>
                {channels
                  .filter((c) => c.kind === 'program')
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </Field>
          )}
          {draft.kind === 'ifb' && (
            <Field label={`Program dim · ${draft.dimDb <= -100 ? 'full cut' : `${draft.dimDb} dB`}`} help="How far program audio drops while someone talks on this IFB.">
              <input className="range" type="range" min={-100} max={0} step={1} value={draft.dimDb} onChange={(e) => setDraft({ ...draft, dimDb: Number(e.target.value) })} />
            </Field>
          )}
          <Field label="Sort order">
            <input className="input" type="number" min={0} value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} />
          </Field>
          <Field label="Colour">
            <SwatchPicker value={draft.color} onChange={(c) => setDraft({ ...draft, color: (c ?? 'cyan') as Swatch })} />
          </Field>
          {error && <p className="error">{error}</p>}
        </Modal>
      )}
    </>
  );
}
